import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import {
    Instagram,
    Facebook,
    Linkedin,
    Share2,
    CheckCircle2,
    ExternalLink,
    Loader2,
    Megaphone,
    Upload,
    Clock,
    XCircle,
    AlertTriangle,
    Music2,
} from "lucide-react";
import api, { formatApiError } from "../lib/api";

const ICON_MAP = {
    instagram: Instagram,
    facebook: Facebook,
    linkedin: Linkedin,
    tiktok: Music2,
    share: Share2,
};

const STATUS_META = {
    none: { label: "À faire", cls: "bg-white/5 text-white/50", icon: null },
    started: { label: "Preuve attendue", cls: "bg-shariti-yellow/20 text-shariti-yellow", icon: Clock },
    pending_review: { label: "En attente de validation", cls: "bg-shariti-orange/20 text-shariti-orange-light", icon: Clock },
    validated: { label: "Validé", cls: "bg-emerald-500/20 text-emerald-400", icon: CheckCircle2 },
    rejected: { label: "Refusé", cls: "bg-red-500/20 text-red-400", icon: XCircle },
    expired: { label: "Expiré (48h)", cls: "bg-white/10 text-white/40", icon: AlertTriangle },
};

function deadlineRemaining(iso) {
    if (!iso) return null;
    try {
        const ms = new Date(iso) - new Date();
        if (ms <= 0) return "expiré";
        const h = Math.floor(ms / 3600000);
        if (h >= 1) return `${h}h restantes`;
        const m = Math.floor(ms / 60000);
        return `${m}min restantes`;
    } catch (e) {
        return null;
    }
}

export default function SocialEngagement({ onPointsChanged }) {
    const [data, setData] = useState(null);
    const [loadingId, setLoadingId] = useState("");
    const fileInputs = useRef({});

    const reload = () => {
        api.get("/social-actions/me").then((r) => setData(r.data));
    };
    useEffect(() => {
        reload();
    }, []);

    const handleStart = async (a) => {
        if (a.url_key && !a.configured) {
            toast.error("Lien pas encore configuré par l'admin.", { duration: 4000 });
            return;
        }
        if (a.url) window.open(a.url, "_blank", "noopener,noreferrer");
        try {
            await api.post(`/social-actions/${a.action_id}/start`);
            toast.success(
                "Ouvre l'app, fais l'action, puis reviens uploader ta capture d'écran (48h max).",
                { duration: 6000 },
            );
            reload();
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || "Erreur");
        }
    };

    const handleProofUpload = async (a, file) => {
        if (!file) return;
        if (file.size > 5 * 1024 * 1024) {
            toast.error("Image trop lourde (5 Mo max)");
            return;
        }
        setLoadingId(a.action_id);
        try {
            const fd = new FormData();
            fd.append("file", file);
            await api.post(`/social-actions/${a.action_id}/upload-proof`, fd, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            toast.success(
                "Preuve envoyée ! Un manager va valider sous peu, tu recevras les points.",
                { duration: 5000 },
            );
            reload();
            onPointsChanged?.();
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || "Upload impossible");
        } finally {
            setLoadingId("");
        }
    };

    if (!data) return null;
    const progress = Math.round((data.earned_points / Math.max(1, data.total_points)) * 100);

    return (
        <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.15 }}
            className="col-span-12 glass-card rounded-3xl p-5 lg:p-6 border border-shariti-orange/20"
            data-testid="social-engagement"
        >
            <div className="flex items-start gap-3 mb-4">
                <div className="w-11 h-11 rounded-2xl bg-shariti-orange/15 flex items-center justify-center shrink-0">
                    <Megaphone className="w-5 h-5 text-shariti-orange" />
                </div>
                <div className="flex-1 min-w-0">
                    <p className="text-[10px] font-thin tracking-[0.3em] uppercase text-shariti-orange-light mb-1">
                        Engagement Shariti
                    </p>
                    <h3 className="text-xl lg:text-2xl font-extrabold text-white tracking-tight leading-tight">
                        Soutiens Shariti, gagne des points
                    </h3>
                    <p className="text-xs text-white/55 font-light mt-1 max-w-2xl">
                        Fais l'action, upload une capture d'écran, un manager valide → les points sont
                        crédités. Tu as 48h pour envoyer la preuve.
                    </p>
                </div>
                <div className="text-right shrink-0">
                    <p className="text-[10px] font-thin tracking-[0.25em] uppercase text-white/40">
                        Progression
                    </p>
                    <p className="text-2xl font-extrabold text-shariti-orange tabular-nums">
                        {data.completion_count}
                        <span className="text-sm text-white/40 font-bold">/{data.total_actions}</span>
                    </p>
                </div>
            </div>

            <div className="h-1 bg-white/5 rounded-full overflow-hidden mb-5">
                <div
                    className="h-full bg-shariti-orange rounded-full transition-all"
                    style={{ width: `${progress}%` }}
                />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {data.actions.map((a) => {
                    const Icon = ICON_MAP[a.icon] || Share2;
                    const status = a.status || (a.completed ? "validated" : "none");
                    const meta = STATUS_META[status] || STATUS_META.none;
                    const StatusIcon = meta.icon;
                    const isLoading = loadingId === a.action_id;
                    const notConfigured = a.url_key && !a.configured;
                    const canUpload =
                        status === "started" ||
                        status === "rejected" ||
                        status === "expired" ||
                        status === "none";
                    const showStartButton = status === "none" || status === "expired";
                    const showProofUpload =
                        status === "started" || status === "rejected" || status === "expired";
                    const remaining =
                        status === "started" ? deadlineRemaining(a.proof_deadline) : null;

                    return (
                        <div
                            key={a.action_id}
                            data-testid={`social-action-${a.action_id}`}
                            className={`p-4 rounded-2xl border transition-all ${
                                status === "validated"
                                    ? "bg-emerald-500/5 border-emerald-500/30"
                                    : status === "pending_review"
                                      ? "bg-shariti-orange/5 border-shariti-orange/30"
                                      : status === "rejected"
                                        ? "bg-red-500/5 border-red-500/20"
                                        : notConfigured
                                          ? "bg-white/[0.02] border-white/5 opacity-60"
                                          : "bg-white/[0.03] border-white/10"
                            }`}
                        >
                            <div className="flex items-start justify-between gap-2 mb-2">
                                <div
                                    className={`w-9 h-9 rounded-xl flex items-center justify-center shrink-0 ${
                                        status === "validated"
                                            ? "bg-emerald-500 text-white"
                                            : "bg-white/5 text-white/70"
                                    }`}
                                >
                                    {isLoading ? (
                                        <Loader2 className="w-4 h-4 animate-spin" />
                                    ) : (
                                        <Icon className="w-4 h-4" />
                                    )}
                                </div>
                                <span className="text-[10px] font-extrabold uppercase tracking-widest px-2 py-0.5 rounded-full shrink-0 bg-shariti-orange/15 text-shariti-orange-light">
                                    +{a.points} pts
                                </span>
                            </div>
                            <p className="text-sm font-extrabold text-white leading-tight mb-1">
                                {a.label}
                            </p>
                            <p className="text-[11px] font-light leading-relaxed text-white/50 mb-3">
                                {notConfigured
                                    ? "Lien à configurer par l'admin — bientôt disponible."
                                    : a.description}
                            </p>
                            {/* Status badge */}
                            <div
                                className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-extrabold uppercase tracking-widest ${meta.cls} mb-2`}
                                data-testid={`social-action-status-${a.action_id}`}
                            >
                                {StatusIcon && <StatusIcon className="w-3 h-3" />}
                                {meta.label}
                                {remaining && <span className="opacity-80 ml-1">· {remaining}</span>}
                            </div>
                            {status === "rejected" && a.rejection_reason && (
                                <p className="text-[11px] text-red-400/90 font-light mb-2 italic">
                                    Raison : {a.rejection_reason}
                                </p>
                            )}
                            {/* Actions */}
                            {!notConfigured && status !== "validated" && status !== "pending_review" && (
                                <div className="flex flex-wrap gap-2 mt-1">
                                    {showStartButton && (
                                        <button
                                            onClick={() => handleStart(a)}
                                            data-testid={`social-action-start-${a.action_id}`}
                                            className="inline-flex items-center gap-1.5 bg-shariti-orange text-white text-xs font-extrabold uppercase tracking-widest px-3 py-2 rounded-xl hover:bg-shariti-orange-light transition-all"
                                        >
                                            <ExternalLink className="w-3.5 h-3.5" />
                                            Ouvrir
                                        </button>
                                    )}
                                    {canUpload && (
                                        <>
                                            <button
                                                onClick={() => fileInputs.current[a.action_id]?.click()}
                                                disabled={isLoading}
                                                data-testid={`social-action-upload-${a.action_id}`}
                                                className="inline-flex items-center gap-1.5 bg-white/5 hover:bg-white/10 border border-white/10 hover:border-shariti-orange/40 text-white text-xs font-extrabold uppercase tracking-widest px-3 py-2 rounded-xl transition-all disabled:opacity-60"
                                            >
                                                <Upload className="w-3.5 h-3.5" />
                                                {showProofUpload ? "Envoyer la preuve" : "Capture"}
                                            </button>
                                            <input
                                                ref={(el) => (fileInputs.current[a.action_id] = el)}
                                                type="file"
                                                accept="image/*"
                                                className="hidden"
                                                onChange={(e) =>
                                                    handleProofUpload(a, e.target.files?.[0])
                                                }
                                            />
                                        </>
                                    )}
                                </div>
                            )}
                            {status === "pending_review" && a.proof_url && (
                                <p className="text-[11px] text-white/50 italic mt-1">
                                    Preuve envoyée — un manager va valider sous peu.
                                </p>
                            )}
                        </div>
                    );
                })}
            </div>
        </motion.div>
    );
}
